import { useEffect, useRef, useState } from "react";

// One persistent element: play() always starts inside the user's gesture.
export const useVoiceAudio = () => {
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const contextRef = useRef(null);
  const selectedRef = useRef(null);
  const requestRef = useRef(0);
  const [activeId, setActiveId] = useState("pulse");
  const [status, setStatus] = useState("idle");
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");

  const getAudio = () => {
    if (audioRef.current) return audioRef.current;
    const audio = new Audio();
    audio.preload = "none";
    audio.ontimeupdate = () => setTime(audio.currentTime);
    audio.onloadedmetadata = () => setDuration(audio.duration);
    audio.onplaying = () => setStatus("playing");
    audio.onwaiting = () => { if (!audio.paused) setStatus("loading"); };
    audio.onended = () => { setStatus("ended"); setTime(0); audio.currentTime = 0; };
    audio.onerror = () => {
      setStatus("error");
      setError("This sample couldn't load. Please try again.");
    };
    audioRef.current = audio;
    return audio;
  };

  const connectAnalyser = (audio) => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    try {
      if (!contextRef.current) {
        const context = new AudioContext();
        const analyser = context.createAnalyser();
        analyser.fftSize = 256;
        context.createMediaElementSource(audio).connect(analyser);
        analyser.connect(context.destination);
        contextRef.current = context;
        analyserRef.current = analyser;
      }
      if (contextRef.current.state === "suspended") contextRef.current.resume().catch(() => {});
    } catch (_) { /* Plain HTML audio remains available without an analyser. */ }
  };

  const toggle = (voice) => {
    const audio = getAudio();
    const request = ++requestRef.current;
    setError("");
    if (selectedRef.current === voice.id && !audio.paused) {
      audio.pause();
      setStatus("paused");
      return;
    }
    if (selectedRef.current !== voice.id || audio.error) {
      audio.pause();
      audio.src = voice.src;
      selectedRef.current = voice.id;
      setTime(0);
      setDuration(voice.duration);
    }
    setActiveId(voice.id);
    setStatus("loading");
    connectAnalyser(audio);
    audio.play().catch((err) => {
      if (request !== requestRef.current || err.name === "AbortError") return;
      setStatus("error");
      setError(err.name === "NotAllowedError" ? "Playback paused by your browser. Press play to listen." : "This sample couldn't play. Please try again.");
    });
  };

  const seek = (value) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = Math.min(Math.max(0, value), audio.duration);
    setTime(audio.currentTime);
    if (audio.paused) setStatus("paused");
  };

  useEffect(() => () => {
    ++requestRef.current;
    const audio = audioRef.current;
    if (audio) {
      audio.onplaying = audio.onwaiting = audio.onended = audio.onerror = audio.ontimeupdate = audio.onloadedmetadata = null;
      audio.pause(); audio.removeAttribute("src"); audio.load();
    }
    if (contextRef.current && contextRef.current.state !== "closed") contextRef.current.close().catch(() => {});
    audioRef.current = null;
    contextRef.current = null;
    analyserRef.current = null;
  }, []);

  return { activeId, status, time, duration, error, toggle, seek, audioRef, analyserRef };
};